import {useState} from 'react';
import {Mail} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {Switch} from '@common-web/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {API_ENVIRONMENTS, type ApiEnvironment} from '@common/constants/environments';
import {
  isToggleableEmailEnvironment,
  resolveEmailSendDecision,
  type OutboundEmailConfig,
  type ToggleableEmailEnvironment,
} from '@common/emailSending';

type OutboundEmailConfigSectionProps = {
  value: OutboundEmailConfig | undefined;
  /** The environment the API answering this page runs in. */
  currentEnvironment: ApiEnvironment | undefined;
  disabled?: boolean;
  onChange: (environment: ToggleableEmailEnvironment, enabled: boolean) => void;
};

export function OutboundEmailConfigSection({
  value,
  currentEnvironment,
  disabled,
  onChange,
}: OutboundEmailConfigSectionProps) {
  const [confirming, setConfirming] = useState<ToggleableEmailEnvironment | null>(null);

  return (
    <div className='space-y-4'>
      <div>
        <div className='flex items-center gap-2'>
          <Mail className='h-4 w-4 text-brand-gray' />
          <h3 className='text-sm font-semibold text-brand-navy'>Outbound email</h3>
        </div>
        <p className='mt-1 text-xs text-brand-gray'>
          Each environment has its own database, so this page can only change the environment its
          API runs in
          {currentEnvironment ? (
            <>
              {' '}
              (<span className='font-medium'>{currentEnvironment}</span>)
            </>
          ) : null}
          . Suppressed emails are logged by the API. A change applies to the next email sent.
        </p>
      </div>

      <div className='divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white px-4'>
        {API_ENVIRONMENTS.map((name) => {
          const isCurrent = name === currentEnvironment;
          const decision = resolveEmailSendDecision(name, value);
          const toggleable = isToggleableEmailEnvironment(name);
          return (
            <div key={name} className='flex items-center justify-between gap-4 py-3'>
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <div className='text-sm font-medium text-brand-navy'>{name}</div>
                  {isCurrent && (
                    <Badge variant='outline' className='text-xs'>
                      this environment
                    </Badge>
                  )}
                </div>
                <div className='text-xs text-brand-gray'>
                  {statusLabel(name, decision.send, isCurrent)}
                </div>
              </div>
              {toggleable ? (
                <Switch
                  checked={decision.send}
                  disabled={disabled || !isCurrent}
                  onCheckedChange={(checked) =>
                    checked ? setConfirming(name) : onChange(name, false)
                  }
                  aria-label={`Outbound email for ${name}`}
                />
              ) : (
                <span className='text-xs text-brand-gray'>
                  {decision.send ? 'Always on' : 'Always off'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send real email from {confirming}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every email the {confirming} API sends from now on (invites, password resets, RFC
              notifications, tickets) goes to its real recipients until this is switched off.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) onChange(confirming, true);
                setConfirming(null);
              }}
            >
              Turn on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function statusLabel(name: ApiEnvironment, sending: boolean, isCurrent: boolean): string {
  if (name === 'local') return 'Never sends. Hardcoded guard in the API.';
  if (name === 'prod') return 'Always sends.';
  const state = sending ? 'Sending' : 'Suppressed';
  return isCurrent ? state : `${state}. Change it from the ${name} internal portal.`;
}
